'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import ReactDOM from 'react-dom';
import cx from 'classnames';
import { toInteger } from 'lodash';
import Handlebars from 'handlebars/dist/cjs/handlebars';
import dayjs from '@utils/dayjs-setup';
import type { ListingBySlugPartial } from '@utils/imperative-node-queries';
import { zIndex } from '@utils/constants';
import { appendUrlParams } from '@utils/append-url-params';
import { formatWholeDollarAmount } from '@utils/money';
import { routes } from '@routes';
import { BookDirectWebsiteUrlParamsTemplateInput, QuoteItemizedType } from '@gql';
import { useBookingWidgetSearchParams } from './use-booking-widget-search-params';
import { AnalyticsRef } from '@components/analytics-ref';
import { Button } from '@components/button';
import { Link } from '@components/link';
import { SavvyPromiseBadge } from '@components/savvy-promise';
import { Spinner } from '@components/spinner';
import { Typography } from '@components/typography';
import { Icon, IconNames } from '@components/icon';
import { QuotesProvider, useQuotes } from './quotes-context';
import { QuoteComparisonProvider } from './quotes-compare/quote-comparison-context';
import { QuotesCompare } from './quotes-compare/quotes-compare';
import { QuotesCta } from './quotes-cta';
import { QuotesModal } from './quotes-modal';
import { ListingTrustpilotCard } from '../trust';

// Register Handlebars helpers once at module scope
Handlebars.registerHelper('formatDate', function (date, format) {
  return dayjs(date).format(format);
});
Handlebars.registerHelper('add', function (...args) {
  const numbers = args.slice(0, -1);
  return Math.round(numbers.reduce((sum, number) => sum + parseFloat(number), 0));
});
interface ListingQuotesSectionProps {
  listing: ListingBySlugPartial;
}
interface ListingQuotesSectionContentProps extends ListingQuotesSectionProps {
  adultCount: number;
  childCount: number;
  infantCount: number;
  petCount: number;
  checkInDate: string | null;
  checkOutDate: string | null;
}
type CtaConfig = {
  label: string;
  /**
   * Lead-in text shown before the property manager company name under
   * the CTA. e.g. `Booking with` for on-platform Savvy bookings,
   * `Stay hosted by` for off-platform "Book with host" listings.
   * Comes from BHT-6145 comps.
   */
  subtext?: string;
  analyticsRef: string;
  bookingUrl?: string;
  showExternalIcon?: boolean;
};
const QuotesComparePortal: React.FC<{
  isVisible: boolean;
}> = t0 => {
  const $ = _c(5);
  const {
    isVisible
  } = t0;
  const [slot, setSlot] = React.useState(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setSlot(document.getElementById("quotes-compare-slot"));
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  React.useEffect(t1, t2);
  if (!slot || !isVisible) {
    return null;
  }
  let t3;
  if ($[2] !== slot) {
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <QuotesCompare />;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    t3 = ReactDOM.createPortal(t4, slot);
    $[2] = slot;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
const ListingQuotesContent: React.FC<ListingQuotesSectionContentProps> = t0 => {
  const $ = _c(112);
  const {
    listing,
    adultCount,
    childCount,
    infantCount,
    petCount,
    checkInDate,
    checkOutDate
  } = t0;
  const {
    activateQuotesModal,
    checkInDate: quotesCheckInDate,
    checkOutDate: quotesCheckOutDate,
    deactivateQuotesModal,
    datesPresent,
    bookingQuotePresent,
    quoteData,
    quoteDataLoading,
    quoteValidationMessage,
    quotesModalActive,
    numberOfNights
  } = useQuotes();
  const shouldShowWebsiteUrlAsButton = !!(listing.marketingWebsiteUrl || listing.account.marketingWebsiteUrl) && !listing.bookDirectWebsiteUrl;
  const shouldShowBookingUrl = !!listing.bookDirectWebsiteUrl;
  const shouldShowRatesAndAvailability = listing.supportsQuotes;
  const canBookOnSavvyCheckout = listing.supportsBookings || listing.supportsIframeBooking;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      label: "",
      analyticsRef: ""
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [cta, setCta] = React.useState(t1);
  let t2;
  if ($[1] !== adultCount || $[2] !== canBookOnSavvyCheckout || $[3] !== checkInDate || $[4] !== checkOutDate || $[5] !== childCount || $[6] !== infantCount || $[7] !== listing.account || $[8] !== listing.bookDirectWebsiteUrl || $[9] !== listing.bookDirectWebsiteUrlParamsTemplate || $[10] !== listing.marketingWebsiteUrl || $[11] !== listing.slug || $[12] !== listing.supportsQuotes || $[13] !== petCount || $[14] !== quoteData || $[15] !== quotesCheckInDate || $[16] !== quotesCheckOutDate || $[17] !== quotesModalActive) {
    t2 = () => {
      const effectCheckIn = quotesCheckInDate || checkInDate;
      const effectCheckOut = quotesCheckOutDate || checkOutDate;
      let bookingUrl;
      if (canBookOnSavvyCheckout && listing.supportsQuotes && listing.slug) {
        bookingUrl = routes.book({
          slug: listing.slug,
          adultCount,
          checkInDate: effectCheckIn ? effectCheckIn : undefined,
          checkOutDate: effectCheckOut ? effectCheckOut : undefined,
          childCount,
          infantCount,
          petCount
        });
      } else {
        if (listing.bookDirectWebsiteUrl && listing.bookDirectWebsiteUrlParamsTemplate) {
          const template = Handlebars.compile(listing.bookDirectWebsiteUrlParamsTemplate);
          const additionalParams = {
            checkInDate: effectCheckIn || "",
            checkOutDate: effectCheckOut || "",
            adultCount,
            childCount,
            infantCount,
            petCount
          };
          const additionalParamsString = template(additionalParams);
          const separator = listing.bookDirectWebsiteUrl.includes("?") ? "&" : "?";
          const redirectUrl = listing.bookDirectWebsiteUrl + separator + additionalParamsString;
          bookingUrl = routes.deeplink(listing.slug, redirectUrl);
        } else {
          if (listing.bookDirectWebsiteUrl) {
            bookingUrl = routes.deeplink(listing.slug, appendUrlParams(listing.bookDirectWebsiteUrl, {
              utm_source: "Savvy",
              utm_medium: "website"
            }));
          } else {
            if (listing.marketingWebsiteUrl) {
              bookingUrl = routes.deeplink(listing.slug, appendUrlParams(listing.marketingWebsiteUrl, {
                utm_source: "Savvy",
                utm_medium: "website"
              }));
            } else {
              if (listing.account.marketingWebsiteUrl) {
                bookingUrl = routes.deeplink(listing.slug, appendUrlParams(listing.account.marketingWebsiteUrl, {
                  utm_source: "Savvy",
                  utm_medium: "website"
                }));
              } else {
                bookingUrl = undefined;
              }
            }
          }
        }
      }
      if (quotesModalActive) {
        setCta({
          label: "Save",
          analyticsRef: "save_dates"
        });
      } else {
        if (listing.supportsQuotes && (!(effectCheckIn && effectCheckOut) || quoteData && quoteData.bookingQuoteByListingAndDates.__typename == "ValidationError")) {
          setCta({
            label: "Check Availability",
            analyticsRef: "check_availability"
          });
        } else {
          if (effectCheckIn && effectCheckOut && canBookOnSavvyCheckout && quoteData && quoteData.bookingQuoteByListingAndDates.__typename == "BookingQuote") {
            setCta({
              label: "Book direct with Savvy",
              subtext: "Booking with",
              analyticsRef: "select_listing_book_now",
              bookingUrl,
              showExternalIcon: false
            });
          } else {
            if ((!listing.supportsQuotes || !canBookOnSavvyCheckout) && bookingUrl) {
              setCta({
                label: "Book with host",
                subtext: "Stay hosted by",
                analyticsRef: "select_listing_book_direct",
                bookingUrl,
                showExternalIcon: true
              });
            } else {
              setCta({
                label: "Book direct with Savvy",
                subtext: "Booking with",
                analyticsRef: "select_listing_book_direct",
                bookingUrl,
                showExternalIcon: false
              });
            }
          }
        }
      }
    };
    $[1] = adultCount;
    $[2] = canBookOnSavvyCheckout;
    $[3] = checkInDate;
    $[4] = checkOutDate;
    $[5] = childCount;
    $[6] = infantCount;
    $[7] = listing.account;
    $[8] = listing.bookDirectWebsiteUrl;
    $[9] = listing.bookDirectWebsiteUrlParamsTemplate;
    $[10] = listing.marketingWebsiteUrl;
    $[11] = listing.slug;
    $[12] = listing.supportsQuotes;
    $[13] = petCount;
    $[14] = quoteData;
    $[15] = quotesCheckInDate;
    $[16] = quotesCheckOutDate;
    $[17] = quotesModalActive;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  let t3;
  if ($[19] !== adultCount || $[20] !== canBookOnSavvyCheckout || $[21] !== checkInDate || $[22] !== checkOutDate || $[23] !== childCount || $[24] !== infantCount || $[25] !== listing || $[26] !== petCount || $[27] !== quoteData || $[28] !== quotesCheckInDate || $[29] !== quotesCheckOutDate || $[30] !== quotesModalActive) {
    t3 = [quotesModalActive, quoteData, quotesCheckInDate, quotesCheckOutDate, checkInDate, checkOutDate, adultCount, childCount, infantCount, petCount, listing, canBookOnSavvyCheckout];
    $[19] = adultCount;
    $[20] = canBookOnSavvyCheckout;
    $[21] = checkInDate;
    $[22] = checkOutDate;
    $[23] = childCount;
    $[24] = infantCount;
    $[25] = listing;
    $[26] = petCount;
    $[27] = quoteData;
    $[28] = quotesCheckInDate;
    $[29] = quotesCheckOutDate;
    $[30] = quotesModalActive;
    $[31] = t3;
  } else {
    t3 = $[31];
  }
  React.useEffect(t2, t3);
  const [totalPriceInfo, setTotalPriceInfo] = React.useState("");
  const [nightlyPriceInfo, setNightlyPriceInfo] = React.useState("");
  let t4;
  let t5;
  if ($[32] !== datesPresent || $[33] !== listing.maxPrice || $[34] !== listing.minPrice || $[35] !== numberOfNights || $[36] !== quoteData || $[37] !== quoteDataLoading || $[38] !== shouldShowRatesAndAvailability) {
    t4 = () => {
      const getPriceRangeText = () => {
        if (listing.maxPrice && listing.minPrice && listing.maxPrice == listing.minPrice) {
          return `${formatWholeDollarAmount(listing.minPrice)}`;
        }
        if (listing.maxPrice && listing.minPrice) {
          return `${formatWholeDollarAmount(listing.minPrice)} - ${formatWholeDollarAmount(listing.maxPrice)}`;
        }
        if (listing.minPrice) {
          return `${formatWholeDollarAmount(listing.minPrice)}`;
        }
        if (listing.maxPrice) {
          return `${formatWholeDollarAmount(listing.maxPrice)}`;
        }
        return "";
      };
      if (shouldShowRatesAndAvailability && !datesPresent) {
        setNightlyPriceInfo("Add dates for prices");
        setTotalPriceInfo("Add dates for prices");
      } else {
        if (shouldShowRatesAndAvailability && datesPresent && quoteData && quoteData.bookingQuoteByListingAndDates.__typename == "BookingQuote" && quoteData.bookingQuoteByListingAndDates.totalCostCents) {
          setNightlyPriceInfo(`${formatWholeDollarAmount(toInteger((quoteData.bookingQuoteByListingAndDates.quoteItems.find(_temp)?.costCents || quoteData.bookingQuoteByListingAndDates.totalCostCents) / numberOfNights))}`);
          setTotalPriceInfo(quoteData.bookingQuoteByListingAndDates.totalWithoutTaxesCents ? formatWholeDollarAmount(toInteger(quoteData.bookingQuoteByListingAndDates.totalWithoutTaxesCents)) : formatWholeDollarAmount(toInteger(quoteData.bookingQuoteByListingAndDates.totalCostCents)));
        } else {
          if (!quoteDataLoading || !shouldShowRatesAndAvailability) {
            const priceRange = getPriceRangeText();
            setNightlyPriceInfo(priceRange);
            setTotalPriceInfo(priceRange);
          } else {
            setNightlyPriceInfo("");
            setTotalPriceInfo("");
          }
        }
      }
    };
    t5 = [shouldShowRatesAndAvailability, datesPresent, quoteData, quoteDataLoading, numberOfNights, listing.minPrice, listing.maxPrice];
    $[32] = datesPresent;
    $[33] = listing.maxPrice;
    $[34] = listing.minPrice;
    $[35] = numberOfNights;
    $[36] = quoteData;
    $[37] = quoteDataLoading;
    $[38] = shouldShowRatesAndAvailability;
    $[39] = t4;
    $[40] = t5;
  } else {
    t4 = $[39];
    t5 = $[40];
  }
  React.useEffect(t4, t5);
  const shouldShowInlineCompare = shouldShowRatesAndAvailability && datesPresent;
  let t6;
  if ($[41] !== shouldShowInlineCompare) {
    t6 = <QuotesComparePortal isVisible={shouldShowInlineCompare} />;
    $[41] = shouldShowInlineCompare;
    $[42] = t6;
  } else {
    t6 = $[42];
  }
  let t7;
  if ($[43] !== deactivateQuotesModal || $[44] !== listing || $[45] !== quotesModalActive) {
    t7 = <QuotesModal isOpen={quotesModalActive} closeModal={deactivateQuotesModal} listing={listing} />;
    $[43] = deactivateQuotesModal;
    $[44] = listing;
    $[45] = quotesModalActive;
    $[46] = t7;
  } else {
    t7 = $[46];
  }
  let t8;
  let t9;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cx("block bg-white fixed w-full z-50 bottom-0 left-0 right-0 border-t md:hidden pt-4 pb-2 border border-gray-light shadow-2xl");
    t9 = {
      zIndex: zIndex.stickyBar
    };
    $[47] = t8;
    $[48] = t9;
  } else {
    t8 = $[47];
    t9 = $[48];
  }
  let t10;
  if ($[49] !== bookingQuotePresent || $[50] !== datesPresent || $[51] !== quoteDataLoading || $[52] !== totalPriceInfo) {
    t10 = totalPriceInfo !== "" ? <div className="flex items-end space-x-1"><Typography variant="body2" variantL="body1" className="flex flex-col items-start whitespace-nowrap font-semibold text-gray-dark">{totalPriceInfo}</Typography><Typography variant="body1" className="mb-[1px] flex flex-col text-xs">{datesPresent ? bookingQuotePresent ? " before taxes" : " per night" : null}</Typography></div> : quoteDataLoading && <Spinner />;
    $[49] = bookingQuotePresent;
    $[50] = datesPresent;
    $[51] = quoteDataLoading;
    $[52] = totalPriceInfo;
    $[53] = t10;
  } else {
    t10 = $[53];
  }
  let t11;
  if ($[54] !== t10) {
    t11 = <div className="flex h-8 flex-col items-start justify-center">{t10}</div>;
    $[54] = t10;
    $[55] = t11;
  } else {
    t11 = $[55];
  }
  let t12;
  if ($[56] !== activateQuotesModal || $[57] !== quotesCheckInDate || $[58] !== quotesCheckOutDate || $[59] !== shouldShowRatesAndAvailability) {
    t12 = shouldShowRatesAndAvailability && <div className="flex flex-col items-start underline pb-1.5"><Button onClick={() => {
        activateQuotesModal();
      }} size="sm" variant="text" data-id="add_dates_button" className="p-0 focus:outline-none ">{quotesCheckInDate && quotesCheckOutDate ? `${dayjs(quotesCheckInDate).format("MMM D")} - ${dayjs(quotesCheckOutDate).format("MMM D")}` : "Add Dates"}</Button></div>;
    $[56] = activateQuotesModal;
    $[57] = quotesCheckInDate;
    $[58] = quotesCheckOutDate;
    $[59] = shouldShowRatesAndAvailability;
    $[60] = t12;
  } else {
    t12 = $[60];
  }
  let t13;
  if ($[61] !== t11 || $[62] !== t12) {
    t13 = <div className="flex-1 text-gray-dark">{t11}{t12}</div>;
    $[61] = t11;
    $[62] = t12;
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  let t14;
  if ($[64] !== activateQuotesModal || $[65] !== canBookOnSavvyCheckout || $[66] !== cta.analyticsRef || $[67] !== cta.bookingUrl || $[68] !== cta.label || $[69] !== deactivateQuotesModal || $[70] !== quoteData || $[71] !== quotesCheckInDate || $[72] !== quotesCheckOutDate || $[73] !== quotesModalActive || $[74] !== shouldShowRatesAndAvailability) {
    t14 = quotesModalActive || shouldShowRatesAndAvailability && !quoteData || shouldShowRatesAndAvailability && quoteData?.bookingQuoteByListingAndDates.__typename == "ValidationError" || shouldShowRatesAndAvailability && !(quotesCheckInDate && quotesCheckOutDate) ? <Button className="whitespace-nowrap" onClick={event => {
      event.stopPropagation();
      if (quotesCheckInDate && quotesCheckOutDate && quotesModalActive) {
        deactivateQuotesModal();
      } else {
        activateQuotesModal();
      }
    }} disabled={quotesModalActive && !(quotesCheckInDate && quotesCheckOutDate)} data-id={cta.analyticsRef} size="sm" isFullWidth={true}>{cta.label}</Button> : cta.bookingUrl && <AnalyticsRef name={cta.analyticsRef}><Link isButton={true} isFullWidth={true} className="whitespace-nowrap" size="sm" to={cta.bookingUrl} target={canBookOnSavvyCheckout ? "" : "_blank"} rel="noreferrer">{cta.label}</Link></AnalyticsRef>;
    $[64] = activateQuotesModal;
    $[65] = canBookOnSavvyCheckout;
    $[66] = cta.analyticsRef;
    $[67] = cta.bookingUrl;
    $[68] = cta.label;
    $[69] = deactivateQuotesModal;
    $[70] = quoteData;
    $[71] = quotesCheckInDate;
    $[72] = quotesCheckOutDate;
    $[73] = quotesModalActive;
    $[74] = shouldShowRatesAndAvailability;
    $[75] = t14;
  } else {
    t14 = $[75];
  }
  let t15;
  if ($[76] !== t14) {
    t15 = <div className="flex flex-col justify-center text-gray-dark"><div>{t14}</div></div>;
    $[76] = t14;
    $[77] = t15;
  } else {
    t15 = $[77];
  }
  let t16;
  if ($[78] !== t13 || $[79] !== t15) {
    t16 = <div className="flex flex-row items-center justify-between px-4">{t13}{t15}</div>;
    $[78] = t13;
    $[79] = t15;
    $[80] = t16;
  } else {
    t16 = $[80];
  }
  let t17;
  if ($[81] !== datesPresent || $[82] !== quoteValidationMessage) {
    t17 = datesPresent && quoteValidationMessage && <div className="mt-2 px-4 text-xs font-light text-primary-main">{quoteValidationMessage}</div>;
    $[81] = datesPresent;
    $[82] = quoteValidationMessage;
    $[83] = t17;
  } else {
    t17 = $[83];
  }
  let t18;
  if ($[84] !== t16 || $[85] !== t17) {
    t18 = <div className={t8} style={t9}>{t16}{t17}</div>;
    $[84] = t16;
    $[85] = t17;
    $[86] = t18;
  } else {
    t18 = $[86];
  }
  let t19;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      zIndex: 1
    };
    $[87] = t19;
  } else {
    t19 = $[87];
  }
  let t20;
  if ($[88] !== cta || $[89] !== listing || $[90] !== nightlyPriceInfo || $[91] !== shouldShowRatesAndAvailability) {
    t20 = !shouldShowRatesAndAvailability ? <div className="pb-2">{nightlyPriceInfo ? <div className="ml-1 space-x-[7px] pt-4 text-primary-main"><Typography component="span" variant="body2" variantL="body1" className="font-semibold lg:font-semibold">{nightlyPriceInfo}</Typography></div> : null}{cta.bookingUrl && <div className="pt-4"><AnalyticsRef name={cta.analyticsRef}><Link isButton={true} isFullWidth={true} size="md" to={cta.bookingUrl} target="_blank" rel="noopener noreferrer" className="w-full flex justify-center md:max-w-md h-12 whitespace-nowrap"><span className="flex items-center justify-center gap-2">{cta.label}{cta.showExternalIcon && <Icon name={IconNames.ARROWUPRIGHTFROMSQUARE} size="sm" />}</span></Link></AnalyticsRef>{cta.subtext && <div className="flex flex-row flex-wrap items-baseline justify-center gap-x-1 whitespace-nowrap pt-2 text-xs text-secondary-dark"><span className="font-light">{cta.subtext}</span><span className="font-medium">{listing.account.companyName}</span></div>}</div>}</div> : <div className="pt-4"><QuotesCta listing={listing} nightlyPriceInfo={nightlyPriceInfo} cta={cta} shouldShowRatesAndAvailability={shouldShowRatesAndAvailability} /></div>;
    $[88] = cta;
    $[89] = listing;
    $[90] = nightlyPriceInfo;
    $[91] = shouldShowRatesAndAvailability;
    $[92] = t20;
  } else {
    t20 = $[92];
  }
  const t21 = shouldShowRatesAndAvailability ? "listing_savvy_promise_badge_desktop" : "listing_savvy_promise_badge_off_platform";
  let t22;
  if ($[93] !== t21) {
    t22 = <div className="pb-4 pt-3"><SavvyPromiseBadge variant="belowCard" analyticsRef={t21} /></div>;
    $[93] = t21;
    $[94] = t22;
  } else {
    t22 = $[94];
  }
  let t23;
  if ($[95] !== shouldShowBookingUrl || $[96] !== shouldShowWebsiteUrlAsButton) {
    t23 = !shouldShowBookingUrl && !shouldShowWebsiteUrlAsButton && <div><Typography component="span" variant="body2" variantM="body1" className="font-semibold md:font-semibold">Contact</Typography></div>;
    $[95] = shouldShowBookingUrl;
    $[96] = shouldShowWebsiteUrlAsButton;
    $[97] = t23;
  } else {
    t23 = $[97];
  }
  let t24;
  if ($[98] !== t23) {
    t24 = <div className="space-y-8 px-2 pb-5">{t23}</div>;
    $[98] = t23;
    $[99] = t24;
  } else {
    t24 = $[99];
  }
  let t25;
  if ($[100] !== t20 || $[101] !== t22 || $[102] !== t24) {
    t25 = <div className="bg-white shadow-xl md:rounded-lg md:border-[1.5px] md:border-primary-main"><div className="px-6 sm:px-4">{t20}{t22}{t24}</div></div>;
    $[100] = t20;
    $[101] = t22;
    $[102] = t24;
    $[103] = t25;
  } else {
    t25 = $[103];
  }
  let t26;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <ListingTrustpilotCard className="mt-4 bg-white border border-gray-light shadow-xl" />;
    $[104] = t26;
  } else {
    t26 = $[104];
  }
  let t27;
  if ($[105] !== t25) {
    t27 = <div className="hidden md:block min-w-[370px] max-w-[400px]" style={t19}><div className="md:sticky md:top-24">{t25}{t26}</div></div>;
    $[105] = t25;
    $[106] = t27;
  } else {
    t27 = $[106];
  }
  let t28;
  if ($[107] !== t18 || $[108] !== t27 || $[109] !== t6 || $[110] !== t7) {
    t28 = <>{t6}{t7}{t18}{t27}</>;
    $[107] = t18;
    $[108] = t27;
    $[109] = t6;
    $[110] = t7;
    $[111] = t28;
  } else {
    t28 = $[111];
  }
  return t28;
};
export const ListingQuotesSection: React.FC<ListingQuotesSectionProps> = t0 => {
  const $ = _c(21);
  const {
    listing
  } = t0;
  const {
    adultCount,
    childCount,
    infantCount,
    petCount,
    checkInDate,
    checkOutDate
  } = useBookingWidgetSearchParams(listing.slug);
  let t1;
  if ($[0] !== adultCount || $[1] !== checkInDate || $[2] !== checkOutDate || $[3] !== childCount || $[4] !== infantCount || $[5] !== listing || $[6] !== petCount) {
    t1 = <ListingQuotesContent listing={listing} adultCount={adultCount} childCount={childCount} infantCount={infantCount} petCount={petCount} checkInDate={checkInDate} checkOutDate={checkOutDate} />;
    $[0] = adultCount;
    $[1] = checkInDate;
    $[2] = checkOutDate;
    $[3] = childCount;
    $[4] = infantCount;
    $[5] = listing;
    $[6] = petCount;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== listing || $[9] !== t1) {
    t2 = <QuoteComparisonProvider listing={listing}>{t1}</QuoteComparisonProvider>;
    $[8] = listing;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== adultCount || $[12] !== checkInDate || $[13] !== checkOutDate || $[14] !== childCount || $[15] !== infantCount || $[16] !== listing.guestCapacity || $[17] !== listing.slug || $[18] !== petCount || $[19] !== t2) {
    t3 = <QuotesProvider listingSlug={listing.slug} guestCapacity={listing.guestCapacity} adultCount={adultCount} childCount={childCount} infantCount={infantCount} petCount={petCount} checkInDate={checkInDate} checkOutDate={checkOutDate}>{t2}</QuotesProvider>;
    $[11] = adultCount;
    $[12] = checkInDate;
    $[13] = checkOutDate;
    $[14] = childCount;
    $[15] = infantCount;
    $[16] = listing.guestCapacity;
    $[17] = listing.slug;
    $[18] = petCount;
    $[19] = t2;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  return t3;
};
function _temp(item) {
  return item.type === QuoteItemizedType.Base;
}